import type { AxiosRequestConfig } from "axios";
import { useNotyf } from "contexts/NotyfContext";
import { useCallback } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { ApiError, isCancelledRequest, wrapApiError } from "../apiErrors";
import type { ApiWriteMethod } from "../cache/types";
import { updateGetCachesAfterMutation } from "../cache/updateAfterMutation";
import { client } from "../client";
import { resolveApiResourceUrl } from "../resources/routeUtils";
import type { ApiResourceRoute } from "../resources/types";
import type {
  ApiDeleteFunc,
  ApiMutationOptions,
  ApiPostDynamicFunc,
  ApiPostFunc,
  ApiPutFunc,
  ApiWriteMutationArg,
  ApiWriteMutationKey,
  ApiWriteMutationResponse,
} from "./types";

function resolveMutationOptions<TResponse, TRequest>(
  options?: ApiMutationOptions<TResponse, TRequest>,
) {
  const { updateCache = true, ...triggerOptions } = options ?? {};
  return { updateCache, triggerOptions };
}

function useApiWriteMutation<TResponse, TRequest>(method: ApiWriteMethod) {
  const { cache, mutate } = useSWRConfig();
  const notyf = useNotyf();
  const key: ApiWriteMutationKey = {
    namespace: "mutation",
    method,
  };

  return useSWRMutation<
    TResponse,
    unknown,
    ApiWriteMutationKey,
    ApiWriteMutationArg<TRequest>
  >(
    key,
    async (mutationKey, { arg }) => {
      const { route, descriptor, collectionRoute } = arg.resourceRoute;
      const clientType = descriptor.clientType;
      const url = resolveApiResourceUrl(arg.resourceRoute);
      if (!url) throw new Error("No API route provided");

      try {
        const requestConfig: AxiosRequestConfig<TRequest> = {
          method: mutationKey.method,
          url,
        };
        if (arg.request != null) requestConfig.data = arg.request;

        const result = await client().request<TResponse>(requestConfig);

        if (arg.updateCache) {
          await updateGetCachesAfterMutation(mutationKey.method, {
            route,
            requestedUrl: url,
            data: result.data,
            descriptor,
            collectionRoute,
            clientType,
            swr: { cache, mutate },
          });
        }

        return result.data;
      } catch (error) {
        const apiError = wrapApiError(error);
        if (apiError instanceof ApiError && !isCancelledRequest(apiError)) {
          notyf.error(apiError.detail);
        }
        throw apiError;
      }
    },
    { populateCache: false, revalidate: false },
  );
}

export function useApiDeleteAsync() {
  const {
    trigger,
    isMutating: isLoading,
    ...response
  } = useApiWriteMutation<void, never>("DELETE");

  const onRequest: ApiDeleteFunc = useCallback(
    (resourceRoute, options) => {
      const { updateCache, triggerOptions } = resolveMutationOptions(options);
      return trigger(
        {
          resourceRoute,
          updateCache,
        },
        triggerOptions,
      );
    },
    [trigger],
  );

  return { ...response, isLoading, onRequest };
}

export function useApiPutAsync<TResponse = any, TRequest = any>() {
  const {
    trigger,
    isMutating: isLoading,
    ...response
  } = useApiWriteMutation<TResponse, TRequest>("PUT");

  const onRequest: ApiPutFunc<TResponse, TRequest> = useCallback(
    (resourceRoute, request, options) => {
      const { updateCache, triggerOptions } = resolveMutationOptions(options);
      return trigger(
        {
          resourceRoute,
          request,
          updateCache,
        },
        triggerOptions,
      );
    },
    [trigger],
  );

  return { ...response, isLoading, onRequest };
}

export function useApiPostAsync<TResponse = any, TRequest = any>(
  resourceRoute: ApiResourceRoute<TResponse>,
): ApiWriteMutationResponse<
  TResponse,
  TRequest,
  ApiPostFunc<TResponse, TRequest>
>;

export function useApiPostAsync<
  TResponse = any,
  TRequest = any,
>(): ApiWriteMutationResponse<
  TResponse,
  TRequest,
  ApiPostDynamicFunc<TResponse, TRequest>
>;

export function useApiPostAsync<TResponse = any, TRequest = any>(
  resourceRoute?: ApiResourceRoute<TResponse>,
): ApiWriteMutationResponse<
  TResponse,
  TRequest,
  ApiPostFunc<TResponse, TRequest> | ApiPostDynamicFunc<TResponse, TRequest>
> {
  const {
    trigger,
    isMutating: isLoading,
    ...response
  } = useApiWriteMutation<TResponse, TRequest>("POST");

  const onRequest = useCallback(
    (
      ...args:
        | [TRequest, ApiMutationOptions<TResponse, TRequest>?]
        | [
            ApiResourceRoute<TResponse>,
            TRequest,
            ApiMutationOptions<TResponse, TRequest>?,
          ]
    ) => {
      if (resourceRoute != null) {
        const [request, options] = args as [
          TRequest,
          ApiMutationOptions<TResponse, TRequest>?,
        ];
        const { updateCache, triggerOptions } = resolveMutationOptions(options);
        return trigger(
          {
            resourceRoute,
            request,
            updateCache,
          },
          triggerOptions,
        );
      }

      const [dynamicResourceRoute, request, options] = args as [
        ApiResourceRoute<TResponse>,
        TRequest,
        ApiMutationOptions<TResponse, TRequest>?,
      ];
      const { updateCache, triggerOptions } = resolveMutationOptions(options);
      return trigger(
        {
          resourceRoute: dynamicResourceRoute,
          request,
          updateCache,
        },
        triggerOptions,
      );
    },
    [resourceRoute, trigger],
  );

  return { ...response, isLoading, onRequest };
}
